/**
 * Client-side prediction and reconciliation for the local player.
 *
 * The local player is simulated immediately on its own input, so movement is
 * not gated on a round trip. The server then tells us where it thinks we
 * actually are, and we replay every input it has not yet acknowledged on top
 * of that to arrive at a corrected present.
 *
 * The whole scheme rests on `step()` behaving identically here and on the
 * server. That is why it lives in `shared/` and why its determinism has its
 * own test.
 */

import {
  INPUT_BUFFER_SIZE,
  RECONCILE_EPSILON,
  RECONCILE_SMOOTH_MS,
  RECONCILE_SNAP_DISTANCE,
  TICK_DT,
  copyMoveState,
  createMoveState,
  lerp,
  positionError,
  seqNewer,
  step,
  type InputFrame,
  type MoveConfig,
  type MoveState,
  type SelfState,
  type StaticWorld,
} from "@prop-hunt/shared";

export interface RenderPose {
  x: number;
  y: number;
  z: number;
}

export class LocalPredictor {
  /** The predicted present. Authoritative for gameplay purposes on this client. */
  readonly state: MoveState;

  /** State before the most recent tick, for render-time smoothing. */
  private readonly previous: MoveState;

  private readonly pending: InputFrame[] = [];
  private readonly stepScratch: number[] = [];
  private readonly replayScratch: number[] = [];
  private readonly replayState: MoveState = createMoveState();

  /**
   * Visual-only offset, decayed toward zero.
   *
   * The simulation adopts the server's correction instantly; only the
   * *rendered* position lags behind, so a small disagreement never yanks the
   * camera. Nothing reads this for gameplay.
   */
  private correctionX = 0;
  private correctionY = 0;
  private correctionZ = 0;

  // Diagnostics, surfaced in the debug overlay.
  lastError = 0;
  lastReplayCount = 0;
  snapCount = 0;
  correctionCount = 0;

  constructor(spawn: MoveState) {
    this.state = copyMoveState(spawn);
    this.previous = copyMoveState(spawn);
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  get correctionMagnitude(): number {
    return Math.sqrt(
      this.correctionX * this.correctionX +
        this.correctionY * this.correctionY +
        this.correctionZ * this.correctionZ,
    );
  }

  /** Advance one tick on local input, and remember it for replay. */
  predict(frame: InputFrame, cfg: MoveConfig, world: StaticWorld): void {
    copyMoveState(this.state, this.previous);
    step(this.state, frame, cfg, world, TICK_DT, this.stepScratch);

    this.pending.push(frame);
    // Bounded: if the server stops acknowledging entirely, we are disconnected
    // and replaying a thousand frames every snapshot helps nobody.
    while (this.pending.length > INPUT_BUFFER_SIZE) this.pending.shift();
  }

  /**
   * Fold in an authoritative snapshot.
   *
   * @param self             server's full-precision state for this client
   * @param lastProcessedSeq newest input seq the server had consumed
   */
  reconcile(
    self: SelfState,
    lastProcessedSeq: number,
    cfg: MoveConfig,
    world: StaticWorld,
  ): void {
    // Everything up to and including the ack is already baked into `self`.
    while (this.pending.length > 0 && !seqNewer(this.pending[0]!.seq, lastProcessedSeq)) {
      this.pending.shift();
    }

    const replayed = this.replayState;
    replayed.x = self.x;
    replayed.y = self.y;
    replayed.z = self.z;
    replayed.vx = self.vx;
    replayed.vy = self.vy;
    replayed.vz = self.vz;
    replayed.onGround = self.onGround;
    // Seeded from the current prediction, then overwritten by the first
    // replayed frame. Only matters when there is nothing to replay.
    replayed.yaw = this.state.yaw;
    replayed.pitch = this.state.pitch;

    for (let i = 0; i < this.pending.length; i++) {
      step(replayed, this.pending[i]!, cfg, world, TICK_DT, this.replayScratch);
    }

    const error = positionError(replayed, this.state);
    this.lastError = error;
    this.lastReplayCount = this.pending.length;

    if (error > RECONCILE_SNAP_DISTANCE) {
      // A genuine desync — respawn, teleport, or a long stall. Easing a five
      // metre correction just makes the player watch themselves glide through
      // a wall, so take it on the chin instead.
      this.correctionX = 0;
      this.correctionY = 0;
      this.correctionZ = 0;
      this.snapCount += 1;
    } else if (error > RECONCILE_EPSILON) {
      // Carry the disagreement into the visual offset so the render position
      // does not move at all this frame, then decay it away.
      this.correctionX += this.state.x - replayed.x;
      this.correctionY += this.state.y - replayed.y;
      this.correctionZ += this.state.z - replayed.z;
      this.correctionCount += 1;
    }
    // Below RECONCILE_EPSILON the disagreement is smaller than a couple of
    // centimetres — adopt it silently rather than spending a smoothing budget.

    // The server is authoritative either way.
    copyMoveState(replayed, this.state);
    copyMoveState(replayed, this.previous);
  }

  /**
   * Take the server's state whole and throw away everything unacknowledged.
   *
   * For discontinuities the client cannot have predicted, where replaying the
   * pending buffer is not merely useless but actively wrong: possession
   * changes the player's hull, so inputs recorded as a 1.8m-tall player would
   * replay through a 1.0m crate and resolve against geometry that never
   * touched them. A respawn is the same shape of problem.
   *
   * The visual correction is dropped rather than decayed, matching the
   * hard-snap branch in `reconcile`: easing a shape change into place just
   * makes the player watch themselves melt.
   */
  adopt(self: SelfState): void {
    this.pending.length = 0;
    this.state.x = self.x;
    this.state.y = self.y;
    this.state.z = self.z;
    this.state.vx = self.vx;
    this.state.vy = self.vy;
    this.state.vz = self.vz;
    this.state.onGround = self.onGround;
    copyMoveState(this.state, this.previous);

    this.correctionX = 0;
    this.correctionY = 0;
    this.correctionZ = 0;
    this.lastError = 0;
    this.lastReplayCount = 0;
  }

  decayCorrection(dtMs: number): void {
    if (dtMs <= 0) return;
    // Decays to ~0.1% of its starting value over RECONCILE_SMOOTH_MS.
    const factor = Math.pow(0.001, dtMs / RECONCILE_SMOOTH_MS);
    this.correctionX *= factor;
    this.correctionY *= factor;
    this.correctionZ *= factor;
  }

  /**
   * Where to actually draw the camera.
   *
   * @param alpha progress through the current tick, in [0, 1]
   *
   * The simulation runs at a fixed 20Hz because it must match the server's
   * step exactly, but rendering at 20Hz would be visibly steppy. Blending
   * between the previous and current predicted states costs up to one tick of
   * positional latency and buys smooth motion at any frame rate.
   *
   * Note that look direction does NOT go through here — yaw and pitch are
   * applied to the camera live, at render rate, so aiming stays instant.
   */
  renderPose(alpha: number, out: RenderPose): RenderPose {
    const t = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    out.x = lerp(this.previous.x, this.state.x, t) + this.correctionX;
    out.y = lerp(this.previous.y, this.state.y, t) + this.correctionY;
    out.z = lerp(this.previous.z, this.state.z, t) + this.correctionZ;
    return out;
  }
}
